class BlueberryConnectivity extends HTMLElement {
  connectedCallback() {
    this.setup = new Map();
    this.currency = this.dataset.currency || 'INR';
    this.mainPriceCents = Number(this.dataset.mainPriceCents || 0);
    this.mainVariantId = this.dataset.mainVariantId;
    this.mainTitle = this.dataset.mainTitle || '';
    this.includeMainInCart = this.dataset.includeMainInCart === 'true';
    this.showMainInSummary = this.dataset.showMainSummary !== 'false';
    this.parentTitle = this.dataset.parentTitle || '';

    this.linesEl = this.querySelector('[data-connectivity-lines]');
    this.emptyEl = this.querySelector('[data-connectivity-empty]');
    this.countEl = this.querySelector('[data-connectivity-item-count]');
    this.accessoriesTotalEl = this.querySelector('[data-connectivity-accessories-total]');
    this.accessoriesLabelEl = this.querySelector('[data-connectivity-accessories-label]');
    this.setupTotalEl = this.querySelector('[data-connectivity-setup-total]');
    this.addAllBtn = this.querySelector('[data-connectivity-add-all]');
    this.lineTemplate = this.querySelector('[data-connectivity-line-template]');
    this.accessoriesLabelBase =
      this.accessoriesLabelEl?.dataset.baseLabel || this.accessoriesLabelEl?.textContent || 'Accessories';

    this.querySelectorAll('[data-connectivity-add-pick]').forEach((button) => {
      button.addEventListener('click', () => this.addPick(button));
    });

    this.addAllBtn?.addEventListener('click', () => this.handleAddAllClick());
    this.updateUI();
    BlueberryConnectivity.active = this;
  }

  disconnectedCallback() {
    if (BlueberryConnectivity.active === this) {
      BlueberryConnectivity.active = null;
    }
  }

  static getInstance() {
    return BlueberryConnectivity.active || document.querySelector('blueberry-connectivity');
  }

  addPick(button) {
    const variantId = button.dataset.variantId;
    if (!variantId) return;

    const existing = this.setup.get(variantId);
    if (existing) {
      existing.qty += 1;
    } else {
      this.setup.set(variantId, {
        variantId,
        priceCents: Number(button.dataset.priceCents || 0),
        title: button.dataset.title || '',
        port: button.dataset.port || '',
        portDetail: button.dataset.portDetail || '',
        image: button.dataset.image || '',
        qty: 1,
      });
    }

    button.classList.add('is-added');
    button.closest('[data-connectivity-pick]')?.classList.add('is-in-setup');
    this.updateUI();
  }

  changeQty(variantId, delta) {
    const item = this.setup.get(variantId);
    if (!item) return;

    item.qty += delta;
    if (item.qty <= 0) {
      this.setup.delete(variantId);
      this.syncPickButtons(variantId, false);
    }
    this.updateUI();
  }

  removeItem(variantId) {
    this.setup.delete(variantId);
    this.syncPickButtons(variantId, false);
    this.updateUI();
  }

  syncPickButtons(variantId, added) {
    this.querySelectorAll(`[data-connectivity-add-pick][data-variant-id="${variantId}"]`).forEach((button) => {
      button.classList.toggle('is-added', added);
      button.closest('[data-connectivity-pick]')?.classList.toggle('is-in-setup', added);
    });
  }

  formatMoney(cents) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-IN', {
        style: 'currency',
        currency: this.currency,
        maximumFractionDigits: 0,
      }).format(cents / 100);
    } catch (error) {
      return (cents / 100).toFixed(0);
    }
  }

  getAccessoriesTotalCents() {
    let total = 0;
    this.setup.forEach((item) => {
      total += item.priceCents * item.qty;
    });
    return total;
  }

  getItemCount() {
    let count = 0;
    this.setup.forEach((item) => {
      count += item.qty;
    });
    return count;
  }

  hasSetupItems() {
    return this.getItemCount() > 0;
  }

  getSetupQueue() {
    const queue = [];
    this.setup.forEach((item) => {
      queue.push({
        id: item.variantId,
        quantity: item.qty,
        property: this.parentTitle,
      });
    });
    return queue;
  }

  dispatchSetupUpdated() {
    document.dispatchEvent(
      new CustomEvent('blueberry:setup-updated', {
        detail: {
          count: this.getItemCount(),
          totalCents: this.getAccessoriesTotalCents(),
        },
      })
    );
  }

  updateUI() {
    const itemCount = this.getItemCount();
    const accessoriesTotal = this.getAccessoriesTotalCents();
    const showMain = this.showMainInSummary && this.mainPriceCents > 0;
    const grandTotal = (showMain ? this.mainPriceCents : 0) + accessoriesTotal;

    if (this.countEl) {
      this.countEl.textContent = `${itemCount} item${itemCount === 1 ? '' : 's'} selected`;
    }

    if (this.emptyEl) {
      this.emptyEl.hidden = itemCount > 0;
    }

    if (this.linesEl) {
      this.linesEl.hidden = itemCount === 0;
      this.renderLines();
    }

    if (this.accessoriesTotalEl) {
      this.accessoriesTotalEl.textContent = this.formatMoney(accessoriesTotal);
    }

    if (this.accessoriesLabelEl) {
      this.accessoriesLabelEl.textContent =
        itemCount > 0 ? `${this.accessoriesLabelBase} (${itemCount})` : this.accessoriesLabelBase;
    }

    if (this.setupTotalEl) {
      this.setupTotalEl.textContent = this.formatMoney(grandTotal);
    }

    if (this.addAllBtn) {
      this.addAllBtn.disabled = itemCount === 0;
    }

    this.dispatchSetupUpdated();
  }

  renderLines() {
    if (!this.linesEl || !this.lineTemplate) return;

    this.linesEl.innerHTML = '';

    this.setup.forEach((item) => {
      const fragment = this.lineTemplate.content.cloneNode(true);
      const portLabel = item.portDetail ? `${item.port} · ${item.portDetail}` : item.port;

      fragment.querySelector('[data-line-port]').textContent = portLabel;
      fragment.querySelector('[data-line-title]').textContent = item.title;
      fragment.querySelector('[data-line-price]').textContent = this.formatMoney(item.priceCents * item.qty);
      fragment.querySelector('[data-line-qty]').textContent = String(item.qty);

      const image = fragment.querySelector('[data-line-image]');
      if (item.image) {
        image.src = item.image;
        image.alt = item.title;
      } else {
        image.hidden = true;
      }

      fragment.querySelector('[data-line-minus]').addEventListener('click', () => {
        this.changeQty(item.variantId, -1);
      });
      fragment.querySelector('[data-line-plus]').addEventListener('click', () => {
        this.changeQty(item.variantId, 1);
      });
      fragment.querySelector('[data-line-remove]').addEventListener('click', () => {
        this.removeItem(item.variantId);
      });

      this.linesEl.appendChild(fragment);
      this.syncPickButtons(item.variantId, true);
    });
  }

  setLoading(isLoading) {
    if (!this.addAllBtn) return;
    this.addAllBtn.classList.toggle('loading', isLoading);
    this.addAllBtn.disabled = isLoading || this.getItemCount() === 0;
  }

  async handleAddAllClick() {
    if (!this.hasSetupItems()) return;

    const bridge = window.BlueberrySetupCartBridge;
    if (bridge && this.mainVariantId) {
      const laptopInCart = await bridge.variantInCart(this.mainVariantId);
      if (!laptopInCart) {
        const choice = await bridge.confirm({
          title: 'Complete your setup?',
          body: 'This laptop is not in your cart yet. Add accessories only, or include the laptop in one order?',
          primaryLabel: 'Add laptop + accessories',
          secondaryLabel: 'Accessories only',
        });

        if (choice === 'cancel') return;

        await this.addAllToCart({
          includeMain: choice === 'bundle',
        });
        return;
      }
    }

    await this.addAllToCart();
  }

  async addAllToCart(options = {}) {
    const includeMain = options.includeMain ?? this.includeMainInCart;
    const queue = [];

    if (includeMain && this.mainVariantId) {
      queue.push({ id: this.mainVariantId, quantity: 1, isMain: true });
    }

    this.getSetupQueue().forEach((entry) => queue.push(entry));

    if (!queue.length) return;

    this.setLoading(true);
    const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    const cartAddUrl = window.routes?.cart_add_url || `${window.Shopify?.routes?.root || '/'}cart/add.js`;

    try {
      let lastResponse = null;

      for (let i = 0; i < queue.length; i += 1) {
        const entry = queue[i];
        const formData = new FormData();
        formData.append('id', entry.id);
        formData.append('quantity', String(entry.quantity));

        if (entry.property && !entry.isMain) {
          formData.append('properties[Added for]', entry.property);
        }

        if (entry.isMain) {
          const mainForm = document.querySelector('[data-type="add-to-cart-form"]');
          const includesInput = mainForm?.querySelector('input[name="properties[Includes]"]');
          if (includesInput?.value) {
            formData.append('properties[Includes]', includesInput.value);
          }
        }

        if (cart && i === queue.length - 1) {
          formData.append(
            'sections',
            cart.getSectionsToRender().map((section) => section.id).join(',')
          );
          formData.append('sections_url', window.location.pathname);
        }

        const config = typeof fetchConfig === 'function' ? fetchConfig('javascript') : { method: 'POST' };
        config.headers = config.headers || {};
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];
        config.body = formData;

        const response = await fetch(cartAddUrl, config);
        lastResponse = await response.json();

        if (lastResponse.status) {
          throw new Error(lastResponse.description || lastResponse.message || 'Add to cart failed');
        }
      }

      if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'connectivity-setup',
          cartData: lastResponse,
        });
      }

      if (cart && lastResponse?.sections) {
        cart.renderContents(lastResponse);
      } else if (!cart) {
        window.location.href = window.routes?.cart_url || '/cart';
      }
    } catch (error) {
      console.error(error);
    } finally {
      this.setLoading(false);
    }
  }
}

BlueberryConnectivity.active = null;
customElements.define('blueberry-connectivity', BlueberryConnectivity);
